/**
 * Split a line's duration into editable hours and minutes.
 *
 * Prefers the "HH:MM" string the backend sends and falls back to the float
 * hours, which is all the payload carries when time_spent is absent.
 */
export const durationParts = (timesheet = {}) => {
  const [rawHours, rawMinutes] = String(timesheet.timeSpent ?? '').split(':');
  if (rawMinutes !== undefined) {
    return { hours: String(Number(rawHours) || 0), minutes: String(Number(rawMinutes) || 0) };
  }
  const total = Math.round((Number(timesheet.hours) || 0) * 60);
  return { hours: String(Math.floor(total / 60)), minutes: String(total % 60) };
};

/**
 * A line's duration in hours.
 *
 * Reads through durationParts, so a payload carrying only "HH:MM" still
 * counts — `hours` is 0 on every line the backend sends without unit_amount.
 */
export const hoursOf = (timesheet = {}) => {
  const { hours, minutes } = durationParts(timesheet);
  return Number(hours) + Number(minutes) / 60;
};

// Back to "H:MM". Rounding to minutes first is what stops 1.999 becoming "1:60".
export const formatHours = (value) => {
  const total = Math.round(value * 60);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Group lines into project → task → hours per day.
 *
 * Days are keyed by the backend's own date string, so no timezone maths sits
 * between a line and the column it lands in.
 */
export const groupByProject = (timesheets) => {
  const projects = new Map();

  timesheets.forEach((line) => {
    const projectName = line.projectName || '—';
    const taskName = line.taskName || `#${line.taskId ?? '-'}`;
    if (!projects.has(projectName)) projects.set(projectName, { project: projectName, tasks: new Map(), byDay: {} });
    const project = projects.get(projectName);
    if (!project.tasks.has(taskName)) project.tasks.set(taskName, { task: taskName, byDay: {} });
    const task = project.tasks.get(taskName);

    const hours = hoursOf(line);
    task.byDay[line.date] = (task.byDay[line.date] || 0) + hours;
    project.byDay[line.date] = (project.byDay[line.date] || 0) + hours;
  });

  return [...projects.values()].map((project) => ({ ...project, tasks: [...project.tasks.values()] }));
};

/**
 * Resolve a typed name back to its id, '' while the name is still incomplete.
 *
 * First match wins on duplicate names. The pickers would need to carry ids of
 * their own before two projects could share one name.
 */
export const idOf = (options, text) =>
  options.find((option) => option.name?.toLowerCase() === text.trim().toLowerCase())?.id ?? '';
